import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, BookOpen, Shield, FileText, ChevronDown, Camera } from 'lucide-react';

/**
 * Reusable Resources Menu Component
 * Can be used as a floating button or inline menu
 * Provides access to Help, Education, Safety, and Guidelines
 */
const ResourcesMenu = ({ variant = 'floating' }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const resources = [
    {
      label: 'Daily Mirror',
      icon: <Camera className="w-4 h-4" />,
      path: '/daily-mirror',
      color: 'text-purple-700',
      hoverBg: 'hover:bg-purple-50'
    },
    {
      label: 'Love Your Way Guide',
      icon: <BookOpen className="w-4 h-4" />,
      path: '/education/love-your-way',
      color: 'text-rose-600',
      hoverBg: 'hover:bg-rose-50'
    },
    {
      label: 'Help Center',
      icon: <HelpCircle className="w-4 h-4" />,
      path: '/help',
      color: 'text-gray-700',
      hoverBg: 'hover:bg-gray-50'
    },
    {
      label: 'Safety Center',
      icon: <Shield className="w-4 h-4" />,
      path: '/safety',
      color: 'text-gray-700',
      hoverBg: 'hover:bg-gray-50'
    },
    {
      label: 'Community Guidelines',
      icon: <FileText className="w-4 h-4" />,
      path: '/guidelines',
      color: 'text-gray-700',
      hoverBg: 'hover:bg-gray-50'
    }
  ];

  const handleClick = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  if (variant === 'floating') {
    return (
      /* NOTE: Emergent preview injects its own floating UI at bottom-right.
         We: (1) raise z-index to max, (2) lift the button up a bit so it doesn't get covered. */
      <div className="fixed bottom-24 right-6 z-[2147483647]">
        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute bottom-16 right-0 mb-2 w-56 surface-card rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 py-2">
            {resources.map((resource, index) => (
              <button
                key={index}
                onClick={() => handleClick(resource.path)}
                className={`flex items-center space-x-3 px-4 py-3 ${resource.color} ${resource.hoverBg} w-full text-left transition-colors`}
              >
                {resource.icon}
                <span className="text-sm font-medium">{resource.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Floating Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center w-14 h-14 bg-gradient-primary text-white rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
          aria-label="Resources Menu"
        >
          {isOpen ? (
            <ChevronDown className="w-6 h-6" />
          ) : (
            <HelpCircle className="w-6 h-6" />
          )}
        </button>
      </div>
    );
  }

  // Inline variant (for footers, sidebars, etc.)
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">Resources</h4>
      {resources.map((resource, index) => (
        <button
          key={index}
          onClick={() => handleClick(resource.path)}
          className={`flex items-center space-x-2 ${resource.color} ${resource.hoverBg} px-3 py-2 rounded-lg w-full text-left text-sm transition-colors`}
        >
          {resource.icon}
          <span>{resource.label}</span>
        </button>
      ))}
    </div>
  );
};

export default ResourcesMenu;
